import dayjs from 'dayjs';

// Plotly draws SVG; past a few thousand points a chart this size gains nothing
// visible and costs seconds of main-thread time (#274).
export const MAX_LINE_POINTS = 2000;
export const MAX_MARKERS = 2000;

const CLOCK = 'HH:mm';

export interface Reading {
    time: string;
    temperature: number | null;
}

interface Series {
    x: string[];
    y: (number | null)[];
}

// Readings are timed 'YYYY-MM-DD HH:mm' (createDataChildren), so the clock can be
// read straight off the string; anything else goes through dayjs.
const clockOf = (time: any): string =>
    typeof time === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(time)
        ? time.slice(11, 16)
        : dayjs(time).format(CLOCK);

// Inside the daily alert window, which may wrap past midnight.
const inWindow = (clock: string, start: string, end: string) =>
    start <= end ? clock >= start && clock <= end : clock >= start || clock <= end;

/**
 * Keeps at most `maxPoints` points of a line, taking the lowest and highest
 * reading of each bucket so spikes and dips survive. A bucket with no reading
 * keeps a null, so the line still breaks where data is missing.
 */
export function downsampleMinMax(series: Series, maxPoints: number): Series {
    const n = series.x.length;
    if (n <= maxPoints) return series;

    const buckets = Math.max(1, Math.floor(maxPoints / 2));
    const size = n / buckets;
    const out: Series = { x: [], y: [] };
    for (let b = 0; b < buckets; b++) {
        const from = Math.floor(b * size);
        const to = Math.min(n, Math.floor((b + 1) * size));
        let lo = -1;
        let hi = -1;
        for (let i = from; i < to; i++) {
            const v = series.y[i];
            if (typeof v !== 'number' || !Number.isFinite(v)) continue;
            if (lo === -1 || v < (series.y[lo] as number)) lo = i;
            if (hi === -1 || v > (series.y[hi] as number)) hi = i;
        }
        if (lo === -1) {
            out.x.push(series.x[from]);
            out.y.push(null);
            continue;
        }
        for (const i of lo === hi ? [lo] : [Math.min(lo, hi), Math.max(lo, hi)]) {
            out.x.push(series.x[i]);
            out.y.push(series.y[i]);
        }
    }
    return out;
}

/** Every k-th point, so a marker trace has at most `max` points. */
export function capPoints(series: Series, max: number): Series {
    const n = series.x.length;
    if (n <= max) return series;
    const stride = Math.ceil(n / max);
    return {
        x: series.x.filter((_, i) => i % stride === 0),
        y: series.y.filter((_, i) => i % stride === 0),
    };
}

interface Limits {
    alertStart?: any;
    alertEnd?: any;
    upperLimit: number;
    lowerLimit: number;
}

/**
 * The three traces of the record-detail chart, in one pass over the readings.
 *
 * The normal/abnormal rules are unchanged from the original four-pass version,
 * including its quirk: for short records (≤ 432 readings) the normal trace is
 * tested against an empty window (which dayjs reads as "now"), so it keeps nearly
 * every point; for long records it drops in-window excursions and keeps every
 * 6th of the rest. Only the cost changed: the window is formatted once instead of
 * three dayjs calls per reading per pass, and the traces are thinned for drawing.
 */
export function buildDetailSeries(details: Reading[], limits: Limits, now = dayjs()) {
    const start = dayjs(limits.alertStart).format(CLOCK);
    const end = dayjs(limits.alertEnd).format(CLOCK);
    const nowClock = now.format(CLOCK);
    const long = details.length > 432;

    const line: Series = { x: [], y: [] };
    const normal: Series = { x: [], y: [] };
    const abnormal: Series = { x: [], y: [] };
    let normalCandidate = 0;

    for (const item of details) {
        const clock = clockOf(item.time);
        const t = item.temperature as number;
        const withinLimits = t <= limits.upperLimit && t >= limits.lowerLimit;
        const alerting = inWindow(clock, start, end);

        line.x.push(item.time);
        line.y.push(item.temperature);

        if (alerting && !withinLimits) {
            abnormal.x.push(item.time);
            abnormal.y.push(item.temperature);
        }

        if (long) {
            if (!alerting || withinLimits) {
                if (normalCandidate % 6 === 0) {
                    normal.x.push(item.time);
                    normal.y.push(item.temperature);
                }
                normalCandidate += 1;
            }
        } else if (clock !== nowClock || withinLimits) {
            normal.x.push(item.time);
            normal.y.push(item.temperature);
        }
    }

    return {
        line: downsampleMinMax(line, MAX_LINE_POINTS),
        normal: capPoints(normal, MAX_MARKERS),
        abnormal: capPoints(abnormal, MAX_MARKERS),
    };
}
